import { getPosthog, shouldEnablePosthog } from './posthogClient';

export const getExperimentVariantOverride = <V extends string>(
  param: string,
  validVariants: readonly V[]
): V | null => {
  if (typeof window === 'undefined') return null;

  const value = new URLSearchParams(window.location.search).get(param);

  return value !== null && validVariants.includes(value as V)
    ? (value as V)
    : null;
};

// PostHog fetches flags asynchronously after init, so `getFeatureFlag`
// returns `undefined` (-> fallback) if read too early. Wait for the
// flags-loaded event before reading; `onFeatureFlags` fires immediately when
// flags are already available. A timeout guarantees the page never waits on a
// stalled flags request.
const FLAGS_LOAD_TIMEOUT_MS = 3000;

type PostHogClient = NonNullable<Awaited<ReturnType<typeof getPosthog>>>;

const waitForFeatureFlags = (
  posthog: PostHogClient,
  { skipSynchronousFire = false }: { skipSynchronousFire?: boolean } = {}
): Promise<void> =>
  new Promise((resolve) => {
    let settled = false;
    const subscription: { unsubscribe?: () => void } = {};

    const finish = () => {
      if (settled) return;
      settled = true;
      clearTimeout(timeout);
      subscription.unsubscribe?.();
      resolve();
    };

    const timeout = setTimeout(finish, FLAGS_LOAD_TIMEOUT_MS);
    let subscribing = true;

    subscription.unsubscribe = posthog.onFeatureFlags(() => {
      if (skipSynchronousFire && subscribing) return;
      finish();
    });

    subscribing = false;

    if (settled) subscription.unsubscribe();
  });

// Reads an experiment variant from PostHog, which FIRES the exposure event
// (`$feature_flag_called`) — so callers must only invoke it when the
// experiment surface genuinely happened. Logged-out visitors are bucketed on
// their anonymous id (sticky for the device); logged-in users are identified
// first so the variant follows their real id across sessions. Falls back
// outside production, when PostHog is disabled (EU), or on any error.
export const readExperimentVariant = async <V extends string>({
  flag,
  validVariants,
  fallback,
  userId,
}: {
  flag: string;
  validVariants: readonly V[];
  fallback: V;
  userId?: string | null;
}): Promise<V> => {
  try {
    if (process.env.NODE_ENV !== 'production') return fallback;

    const enabled = await shouldEnablePosthog();
    if (!enabled) return fallback;

    const posthog = await getPosthog();
    if (!posthog) return fallback;

    const skipSynchronousFire = Boolean(
      userId && posthog.get_distinct_id?.() !== userId
    );

    if (skipSynchronousFire && userId) {
      posthog.identify(userId);
    }

    await waitForFeatureFlags(posthog, { skipSynchronousFire });

    const value = posthog.getFeatureFlag(flag);

    return typeof value === 'string' && validVariants.includes(value as V)
      ? (value as V)
      : fallback;
  } catch {
    return fallback;
  }
};
